// Tokens der Pillen-Formulare (Handoff Pillen-Formulare §3–§6). Werte aus dem Prototyp
// (`heads`, Abschnitts-META, Feld- und Knopf-Stile). Formulare verwenden nur diese Werte.

import {
  ArrowBendUpLeft, ArrowBendUpRight, ArrowRight, CalendarCheck, Check, FileCsv, LinkSimple,
  PencilSimple, Plus, UploadSimple, type Icon,
} from '@phosphor-icons/react';
import { FARBE } from '../pille-stil';

/** Farbe des App-Icons im Formularkopf (Prototyp `heads`: grad, glow, tint, badgeC). */
export type FormFarbe = 'blau' | 'hellblau' | 'mailblau' | 'gruen' | 'gruenDunkel' | 'rot' | 'orange' | 'gelb' | 'violett' | 'gold';

export const KOPF_FARBE: Record<FormFarbe, { verlauf: string; glow: string; toenung: string; badge: string }> = {
  blau:        { verlauf: 'linear-gradient(160deg,#6aa8ff,#2f6fe0)', glow: 'rgba(47,111,224,0.8)',  toenung: 'rgba(47,111,224,0.22)', badge: '#2f6fe0' },
  hellblau:    { verlauf: 'linear-gradient(160deg,#8cb8ff,#2f6fe0)', glow: 'rgba(47,111,224,0.8)',  toenung: 'rgba(47,111,224,0.2)',  badge: '#2f6fe0' },
  mailblau:    { verlauf: 'linear-gradient(160deg,#64b5ff,#0a6cff)', glow: 'rgba(10,108,255,0.8)',  toenung: 'rgba(10,108,255,0.22)', badge: '#0a6cff' },
  gruen:       { verlauf: 'linear-gradient(160deg,#5fe39a,#1f9d55)', glow: 'rgba(31,157,85,0.8)',   toenung: 'rgba(31,157,85,0.2)',   badge: '#1f9d55' },
  gruenDunkel: { verlauf: 'linear-gradient(160deg,#7fe0a3,#1f8f52)', glow: 'rgba(31,143,82,0.8)',   toenung: 'rgba(31,143,82,0.2)',   badge: '#1f8f52' },
  rot:         { verlauf: 'linear-gradient(160deg,#ff8a80,#e5484d)', glow: 'rgba(229,72,77,0.8)',   toenung: 'rgba(229,72,77,0.2)',   badge: '#e5484d' },
  orange:      { verlauf: 'linear-gradient(160deg,#ffb36b,#e0641a)', glow: 'rgba(224,100,26,0.8)',  toenung: 'rgba(224,100,26,0.2)',  badge: '#e0641a' },
  gelb:        { verlauf: 'linear-gradient(160deg,#ffd56b,#d08a12)', glow: 'rgba(208,138,18,0.8)',  toenung: 'rgba(208,138,18,0.2)',  badge: '#d08a12' },
  violett:     { verlauf: 'linear-gradient(160deg,#c9a8ff,#7a52e0)', glow: 'rgba(122,82,224,0.8)',  toenung: 'rgba(122,82,224,0.22)', badge: '#7a52e0' },
  gold:        { verlauf: 'linear-gradient(160deg,#f1dcb4,#c39556)', glow: 'rgba(195,149,86,0.8)',  toenung: 'rgba(195,149,86,0.22)', badge: '#c39556' },
};

/** Kleines Badge unten rechts am App-Icon (Plus beim Anlegen, Stift beim Bearbeiten …). */
export type FormBadge = 'plus' | 'stift' | 'pfeil' | 'haken' | 'link' | 'kalender' | 'csv' | 'hochladen' | 'antworten' | 'weiterleiten';

export const BADGE_ICON: Record<FormBadge, Icon> = {
  plus: Plus, stift: PencilSimple, pfeil: ArrowRight, haken: Check, link: LinkSimple, kalender: CalendarCheck,
  csv: FileCsv, hochladen: UploadSimple, antworten: ArrowBendUpLeft, weiterleiten: ArrowBendUpRight,
};

/** Optionales viertes Argument von openForm. */
export interface FormMeta {
  untertitel?: string;
  icon?: Icon;
  farbe?: FormFarbe;
  badge?: FormBadge;
  /** „* Pflichtfeld“ im Kopf – nur wenn das Formular Pflichtfelder hat. */
  pflichtHinweis?: boolean;
}

/** Farbe einer Abschnittskarte (Prototyp Abschnitts-META: Verlauf, Tönung, Icon-Farbe). */
export type AbschnittFarbe = 'blau' | 'rot' | 'gold' | 'gruen' | 'orange' | 'violett';

export const ABSCHNITT_FARBE: Record<AbschnittFarbe, { verlauf: string; toenung: string; akzent: string }> = {
  blau:    { verlauf: 'linear-gradient(160deg,#6aa8ff,#2f6fe0)', toenung: 'rgba(106,168,255,0.16)', akzent: '#8cb8ff' },
  rot:     { verlauf: 'linear-gradient(160deg,#ff8a80,#e0453a)', toenung: 'rgba(255,120,110,0.14)', akzent: '#ff8a80' },
  gold:    { verlauf: 'linear-gradient(160deg,#f1dcb4,#c39556)', toenung: 'rgba(226,192,141,0.16)', akzent: FARBE.gold },
  gruen:   { verlauf: 'linear-gradient(160deg,#5fe39a,#1f9d55)', toenung: 'rgba(95,211,138,0.15)',  akzent: '#7fe0a3' },
  orange:  { verlauf: 'linear-gradient(160deg,#ffc56b,#e07b00)', toenung: 'rgba(255,179,64,0.14)',  akzent: '#ffb340' },
  violett: { verlauf: 'linear-gradient(160deg,#c9a8ff,#7a52e0)', toenung: 'rgba(191,150,255,0.14)', akzent: '#d59bff' },
};

/** Icon-Kacheln in Listenzeilen (Prototyp `optRow`: icBg + icC). */
export const ZEILEN_ICON = {
  neutral: { flaeche: 'rgba(255,255,255,0.1)', farbe: '#fff' },
  gedaempft: { flaeche: 'rgba(255,255,255,0.1)', farbe: 'rgba(255,255,255,0.7)' },
  blau: { flaeche: 'rgba(100,160,255,0.2)', farbe: '#8cb8ff' },
  gruen: { flaeche: 'rgba(95,211,138,0.18)', farbe: '#7fe0a3' },
  violett: { flaeche: 'rgba(191,90,242,0.2)', farbe: '#d59bff' },
  gold: { flaeche: 'rgba(226,192,141,0.16)', farbe: FARBE.gold },
  /** Dunkle Kachel mit farbigem Icon (Prototyp Termin-Typ, Priorität) */
  dunkel: { flaeche: 'rgba(255,255,255,0.08)', farbe: '#fff' },
  /** „Nichts gewählt“-Zeile (Prototyp ph-minus) */
  leer: { flaeche: 'rgba(255,255,255,0.1)', farbe: 'rgba(255,255,255,0.4)' },
} as const;

/** Hintergrund, Text und Signalfarben im Formular. */
export const FORM = {
  hintergrund: 'linear-gradient(180deg,#1c1c1e,#141416)',
  linie: 'rgba(255,255,255,0.09)',
  text: '#fff',
  text2: 'rgba(255,255,255,0.5)',
  text3: 'rgba(255,255,255,0.45)',
  gold: FARBE.gold,
  rot: '#ff7b72',
  ok: '#5fd38a',
} as const;

/** Feld-Zustände (Prototyp `base()`): Ruhe, Fokus (goldener Innenring), Fehler (roter Ring). */
export const FELD = {
  radius: 15,
  ruhe: {
    flaeche: 'rgba(0,0,0,0.22)',
    ring: 'inset 0 1px 2px rgba(0,0,0,0.35),inset 0 0 0 0.5px rgba(255,255,255,0.06),0 1px 0 rgba(255,255,255,0.05)',
    label: 'rgba(255,255,255,0.5)',
    icon: 'rgba(255,255,255,0.35)',
  },
  fokus: {
    flaeche: 'rgba(0,0,0,0.34)',
    ring: 'inset 0 1px 2px rgba(0,0,0,0.4),inset 0 0 0 1px rgba(226,192,141,0.75),0 0 0 4px rgba(226,192,141,0.12)',
    label: FARBE.gold,
    icon: FARBE.gold,
  },
  fehler: {
    flaeche: 'rgba(255,95,87,0.1)',
    ring: 'inset 0 0 0 1px rgba(255,95,87,0.6),0 0 0 4px rgba(255,95,87,0.1)',
    label: '#ff7b72',
    icon: '#ff7b72',
  },
  liste: 'rgba(0,0,0,0.28)',
  listeRing: 'inset 0 0 0 0.5px rgba(255,255,255,0.07)',
  zeileAktiv: 'rgba(226,192,141,0.1)',
} as const;

/** Abschnittskarte (iOS-Einstellungen). */
export const ABSCHNITT = {
  radius: 22,
  schatten: 'inset 0 1px 0 rgba(255,255,255,0.13),inset 0 0 0 0.5px rgba(255,255,255,0.07),0 18px 34px -20px rgba(0,0,0,0.8),0 2px 6px -2px rgba(0,0,0,0.35)',
  zaehler: 'rgba(255,255,255,0.55)',
  zaehlerFlaeche: 'rgba(255,255,255,0.07)',
  fehltFlaeche: 'rgba(255,95,87,0.14)',
} as const;

/** Segmentschalter: aktive Option ist eine weiße Pille mit dunkler Schrift. */
export const SEGMENT = {
  behaelter: 'rgba(0,0,0,0.3)',
  behaelterRing: 'inset 0 1px 2px rgba(0,0,0,0.4),0 0.5px 0 rgba(255,255,255,0.06)',
  aktiv: 'rgba(255,255,255,0.92)',
  aktivText: '#111',
  aktivSchatten: '0 2px 6px -1px rgba(0,0,0,0.35)',
  text: 'rgba(255,255,255,0.72)',
} as const;

/** Kacheln für wenige gewichtige Optionen. */
export const KACHEL = {
  ruhe: 'linear-gradient(160deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))',
  ruheRing: 'inset 0 1px 0 rgba(255,255,255,0.1),inset 0 0 0 0.5px rgba(255,255,255,0.07),0 6px 14px -8px rgba(0,0,0,0.6)',
  aktiv: 'linear-gradient(160deg,rgba(226,192,141,0.22),rgba(226,192,141,0.06))',
  aktivRing: 'inset 0 0 0 1.5px #e2c08d,0 10px 20px -10px rgba(226,192,141,0.6)',
  fehlerRing: 'inset 0 0 0 1px rgba(255,95,87,0.5)',
  iconRuhe: 'linear-gradient(160deg,rgba(255,255,255,0.22),rgba(255,255,255,0.08))',
  codeAktiv: '#f1dcb4',
} as const;

/** Rote Fehlerleiste oben im Formular. */
export const FEHLER = {
  flaeche: 'rgba(255,95,87,0.12)',
  ring: 'inset 0 0 0 0.5px rgba(255,95,87,0.35)',
  text: '#ffd1cd',
  icon: '#ff7b72',
  /** Deckende Fläche der Lösch-Rückfrage über dem Mülleimer */
  blase: '#2a1614',
} as const;

/** Knopfleiste (Prototyp `btn()`): Hauptknopf Gold-Verlauf, Abbrechen grau, gesperrt grau. */
export const KNOPF = {
  hoehe: 46,
  radius: 14,
  gold: { flaeche: 'linear-gradient(160deg,#f1dcb4,#d4ad73 55%,#9c7440)', text: '#231806', schatten: 'inset 0 1px 1px rgba(255,255,255,0.45),0 8px 18px -8px rgba(212,173,115,0.6)' },
  gefahr: { flaeche: 'linear-gradient(160deg,#ff8a80,#d93a30)', text: '#fff', schatten: 'inset 0 1px 1px rgba(255,255,255,0.35),0 8px 18px -8px rgba(217,58,48,0.7)' },
  grau: { flaeche: 'rgba(255,255,255,0.1)', text: '#fff', schatten: 'none' },
  gesperrt: { flaeche: 'rgba(255,255,255,0.06)', text: 'rgba(255,255,255,0.32)', schatten: 'none' },
  leiste: 'linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.01))',
  leisteSchatten: '0 -12px 24px -14px rgba(0,0,0,0.6)',
  leisteLinie: 'rgba(255,255,255,0.08)',
  /** Deckender Grund unter der Leiste (unteres Ende des Formular-Hintergrunds). */
  leisteGrund: '#141416',
} as const;

/** iOS-Schalter im Formular: an = Gold-Verlauf (Prototyp `SW`). */
export const SCHALTER_AN = 'linear-gradient(180deg,#f1dcb4,#c39556)';

/** Glanz auf App-Icons und Icon-Kacheln. */
export const GLANZ = 'linear-gradient(180deg,rgba(255,255,255,0.38),rgba(255,255,255,0) 48%)';

/** Kleine Chips (Prototyp Belegung `QL`, Hinweis-Farben): Schrift + Fläche. */
export const CHIP = {
  gold:    { text: FARBE.gold, flaeche: 'rgba(226,192,141,0.15)' },
  gruen:   { text: '#7fe0a3', flaeche: 'rgba(52,199,89,0.15)' },
  blau:    { text: '#8cb8ff', flaeche: 'rgba(10,132,255,0.15)' },
  orange:  { text: '#ffc56b', flaeche: 'rgba(255,159,10,0.15)' },
  violett: { text: '#d59bff', flaeche: 'rgba(191,90,242,0.15)' },
  rot:     { text: '#ff7b72', flaeche: 'rgba(255,95,87,0.14)' },
} as const;
export type ChipFarbe = keyof typeof CHIP;

/** Innere Fläche (Zeilen, Tabellen, Listen in Abschnitten). */
export const INNEN = {
  flaeche: 'rgba(0,0,0,0.22)',
  ring: 'inset 0 0 0 0.5px rgba(255,255,255,0.06)',
  kopf: 'rgba(255,255,255,0.04)',
  linie: 'rgba(255,255,255,0.06)',
  aufklapp: 'rgba(0,0,0,0.25)',
} as const;

/** Wechsel-/Optionskarten (Prototyp `isWechsel`). */
export const OPTION_KARTE = {
  flaeche: 'rgba(255,179,64,0.06)',
  ring: 'inset 0 0 0 0.5px rgba(255,179,64,0.25)',
  kopf: '#ffd38a',
  aus: 'rgba(255,255,255,0.04)',
  ausRing: 'inset 0 0 0 0.5px rgba(255,255,255,0.08)',
  an: 'rgba(255,179,64,0.12)',
  anRing: 'inset 0 0 0 1px rgba(255,179,64,0.55)',
  punktAus: 'inset 0 0 0 1.5px rgba(255,255,255,0.35)',
  punktAn: '#ffb340',
  punktKern: '#2a1500',
} as const;

/** Schrittanzeige (Prototyp CSV-Import). */
export const SCHRITT = {
  aktiv: { flaeche: 'linear-gradient(160deg,#f1dcb4,#c39556)', text: '#fff', schatten: '0 0 0 4px rgba(226,192,141,0.18),inset 0 1px 1px rgba(255,255,255,0.45)', label: '#fff' },
  fertig: { flaeche: 'linear-gradient(160deg,#5fe39a,#1f9d55)', text: '#fff', schatten: 'inset 0 1px 1px rgba(255,255,255,0.4)', label: 'rgba(255,255,255,0.7)' },
  offen: { flaeche: 'rgba(255,255,255,0.08)', text: 'rgba(255,255,255,0.45)', schatten: 'inset 0 0 0 0.5px rgba(255,255,255,0.12)', label: 'rgba(255,255,255,0.4)' },
  linieFertig: '#5fd38a',
  linieOffen: 'rgba(255,255,255,0.1)',
} as const;

/** Chip-Optik der kompakten Auswahl (Prototyp CSV-Mapping „DB-Feld“). */
export const AUSWAHL_KOMPAKT = { flaeche: 'rgba(226,192,141,0.12)', ring: 'inset 0 0 0 0.5px rgba(226,192,141,0.3)' } as const;

/** Farbpunkte der Ticket-Priorität (Prototyp `PR`) – ersetzen die Emojis der alten Auswahl. */
export const PRIO_FARBE = {
  niedrig: '#b9c0cc',
  mittel: '#ffd56b',
  hoch: '#ff9f43',
  kritisch: '#ff5f57',
} as const;

/** Runde Aktionsknöpfe in Eintragskarten (Kopieren, Mail, Bearbeiten). */
export const RUND_KNOPF = {
  flaeche: 'rgba(255,255,255,0.09)',
  ring: 'inset 0 0.5px 0 rgba(255,255,255,0.14)',
} as const;

/** App-Icon-Verläufe im Werkzeug-Dock des E-Mail-Formulars (Prototyp `G`). */
export const DOCK_VERLAUF = {
  anhang: 'linear-gradient(160deg,#a9b6c9,#5b6b84)',
  vorlage: 'linear-gradient(160deg,#ffc56b,#e07b00)',
  signatur: 'linear-gradient(160deg,#9fa8ff,#5856d6)',
  briefpapier: 'linear-gradient(160deg,#f1dcb4,#c39556)',
  akten: 'linear-gradient(160deg,#5ad1c4,#138a7e)',
  ki: 'linear-gradient(135deg,#a77bff,#ff6fae)',
  spaeter: 'linear-gradient(160deg,#8ec5ff,#3a7bd5)',
} as const;

/** Schwebendes Feld über der Knopfleiste (Werkzeug-Popover). */
export const POPOVER = {
  flaeche: 'rgba(30,30,36,0.97)',
  schatten: 'inset 0 1px 0 rgba(255,255,255,0.12),inset 0 0 0 0.5px rgba(255,255,255,0.1),0 30px 60px -16px rgba(0,0,0,0.85)',
  innen: 'rgba(0,0,0,0.3)',
  zeileAktiv: 'rgba(226,192,141,0.12)',
} as const;
